import { selectCurrentTeam, selectInvoiceLimit, selectIsActionsBlocked, showNotifier } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreateInvoiceV2Mutation, useLazyGetCustomerByIdQuery } from '../api';
import { PreviewBar } from '../components';
import { TaxesAndFeesResponse } from '../invoices.model';
import {
  addInvoiceValidationError,
  enableCustomerNotSelectedError,
  initialiseInvoicePaymentModes,
  resetInvoiceState,
  selectHasEstimateRelatedJob,
  selectInvoice,
  selectInvoiceDate,
  selectInvoiceFinalAmount,
  selectInvoiceFromEstimateNumber,
  selectInvoiceFromEstimateStatus,
  selectInvoiceFromJobNumber,
  selectInvoicePaymentModes,
  selectInvoiceReferenceNumber,
  selectIsInitialisingSliceData,
  selectIsInvoiceFromEstimate,
  selectIsInvoiceFromJob,
  selectIsSignatureEnabled,
  selectPartialPaymentEnabled,
  updateDueDate,
  updateInvoiceCreatedResponse,
  updateInvoiceCustomer,
  updateInvoiceDefaultReminderFromSetting,
  updateInvoiceReferenceNumber,
  updateIsSignatureEnabled,
  updatePartialPaymentEnabled,
  updatePaymentReminders,
  updatePaymentRemindersEnabled
} from '../InvoicesSlice';
import InvoiceOptionsSection from './components/InvoiceOptionsSection';
import InvoicesTabSideNav from './components/InvoicesTabSideNav';
import LineItemsSection from './components/LineItemsSection';
import NotesAttachmentsSection from './components/NotesAttachmentsSection';
import PaymentOptionsSection from './components/PaymentOptionsSection';
import InvoiceFormRemindersSection from './components/InvoiceFormRemindersSection';
import { InvoiceTabEnum, minimumLineItemsError } from './constants';

import routesPath from 'routes/RoutesPath';
import { InvoicesRouteState } from 'routes/types';
import { NotifierTypes } from 'constants/notifierConstants';
import queryString from 'query-string';
import { throttle } from 'utils/commonUtils';
import { ErrorResponse } from 'containers/app/app.model';
import { logAnalyticEvent } from 'utils/analytics';
import {
  checkIfAnyPaymentModeIsEligible,
  checkIfInvoiceTotalExceedsPaymentModeLimit,
  getIsSignatureEnabledState,
  hasChangesToSync,
  recalculateReminderDates
} from '../utils';
import { DispatchDestination, invoiceMinimumAmount } from 'constants/common';
import { useGetItemSuggestionsAndGlobalTaxesAndFeesQuery } from 'containers/product-catalog/api';
import MoreOptionsSection from './components/MoreOptionsSection';
import { useGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { FeatureToggleState, PartialPaymentOption } from 'containers/settings/constants';
import { useLazyGetInvoiceSettingsQuery } from 'containers/settings/invoice-settings/api';
import { transformSettingApiToComponentReminders } from 'containers/settings/utils';
import { useLazyGetJobByIdQuery } from 'containers/jobs/api';
import CreateInvoiceCustomer from './components/CreateInvoiceCustomer';
import { GenericModal, Loader } from 'components';
import { CrossIconvector } from 'assets/icons';
import NoSelfCheckoutPopup from '../components/NoSelfCheckoutPopup';
import InvoiceErrorsPopup from '../components/InvoiceErrorsPopup';
import InvoiceLimitExceededPopup from '../components/InvoiceLimitExceededPopup';
import PartialPaymentAvailablePopup from '../components/PartialPaymentAvailablePopup';
import { InvoiceErrorFieldsDefault } from './types';
import { updateCustomizations } from 'containers/settings/invoiceSettingsSlice';
import { useLazyGetEstimateByIdQuery } from 'containers/estimates/api';
import { EstimateStatus } from 'containers/estimates/constants';
import BlockedInfoPopup from 'containers/home/components/BlockedInfoPopup';
import { logUserNetworkData } from 'utils/analytics/utils';
import { getCreateInvoiceV2Payload, getDueDate } from './util';
import { RelatedEntityData } from './types';
import DateUtils from 'utils/dateUtils';

interface ICreateInvoiceContext {
  selectedInvoiceTab: InvoiceTabEnum;
  setSelectedInvoiceTab: React.Dispatch<React.SetStateAction<InvoiceTabEnum>>;
}

interface CreateInvoiceProps {
  onPreviewClick: () => void;
  taxesAndFeesData: TaxesAndFeesResponse;
  isLoadingTaxesAndFees: boolean;
}

export const CreateInvoiceContext = createContext<ICreateInvoiceContext>({
  selectedInvoiceTab: InvoiceTabEnum.BILL_TO,
  setSelectedInvoiceTab: null
});

const CreateInvoice: FC<CreateInvoiceProps> = ({ onPreviewClick, taxesAndFeesData, isLoadingTaxesAndFees }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);
  const selectedInvoice = useAppSelector(selectInvoice);
  const selectedIsSignatureEnabled = useAppSelector(selectIsSignatureEnabled);
  const invoiceReferenceNumber = useAppSelector(selectInvoiceReferenceNumber);
  const isInvoiceFromJob = useAppSelector(selectIsInvoiceFromJob);
  const invoiceFromJobNumber = useAppSelector(selectInvoiceFromJobNumber);
  const invoiceFromEstimateNumber = useAppSelector(selectInvoiceFromEstimateNumber);
  const isInitialising = useAppSelector(selectIsInitialisingSliceData);
  const isInvoiceFromEstimate = useAppSelector(selectIsInvoiceFromEstimate);
  const estimateStatus = useAppSelector(selectInvoiceFromEstimateStatus);
  const hasEstimateRelatedJob = useAppSelector(selectHasEstimateRelatedJob);
  const isEstimatePending = useMemo(() => estimateStatus?.toUpperCase() === EstimateStatus.PENDING, [estimateStatus]);
  const invoiceDateFromState = useAppSelector(selectInvoiceDate);
  const partialPaymentEnabled = useAppSelector(selectPartialPaymentEnabled);
  const invoicePaymentModes = useAppSelector(selectInvoicePaymentModes);
  const invoiceTotal = useAppSelector(selectInvoiceFinalAmount);
  const invoiceLimits = useAppSelector(selectInvoiceLimit);

  const invoiceDate = useMemo(
    () => (invoiceDateFromState ? DateUtils.parseISODateString(invoiceDateFromState) : DateUtils.getNow()),
    [invoiceDateFromState]
  );
  //mandatory data for the invoice
  const hasUserAndLineItemsData = useMemo(
    () => selectedInvoice.invoiceCustomer !== null || selectedInvoice.invoiceLineItems.length > 0,
    [selectedInvoice.invoiceCustomer, selectedInvoice.invoiceLineItems.length]
  );

  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [showSyncConfirmationPopup, setShowSyncConfirmationPopup] = useState(false);
  const [showNoSelfCheckoutPopup, setShowNoSelfCheckoutPopup] = useState(false);
  const [showInvoiceErrorsPopup, setShowInvoiceErrorsPopup] = useState({
    open: false,
    errors: InvoiceErrorFieldsDefault
  });
  const [showMerchantBlockedPopup, setShowMerchantBlockedPopup] = useState(false);
  const [showPartialPaymentAvailablePopup, setShowPartialPaymentAvailablePopup] = useState(false);
  const [showInvoiceLimitExceededPopup, setShowInvoiceLimitExceededPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useGetItemSuggestionsAndGlobalTaxesAndFeesQuery({
    teamId: currentTeam.id,
    profession: currentTeam.profession,
    dispatchDestination: DispatchDestination.INVOICE_SLICE
  });

  const {
    data: merchantCustomisations,
    isSuccess: merchantCustomisationsSuccess,
    error: merchantCustomizationsError,
    isError: isMerchantCustomizationsError
  } = useGetMerchantCustomizationsQuery({
    teamId: currentTeam.id.toString()
  });

  const [getInvoiceSettings] = useLazyGetInvoiceSettingsQuery();

  useEffect(() => {
    if (merchantCustomisationsSuccess && !hasUserAndLineItemsData) {
      dispatch(updateIsSignatureEnabled(getIsSignatureEnabledState(merchantCustomisations.customerSignatureConfig)));
      dispatch(updateCustomizations(merchantCustomisations));
      dispatch(initialiseInvoicePaymentModes(merchantCustomisations.paymentModes));
    }
  }, [merchantCustomisationsSuccess, dispatch]);

  // Fetch invoice settings on mount
  useEffect(() => {
    if (currentTeam.id) {
      getInvoiceSettings({ teamId: currentTeam.id }).then(response => {
        if (response.data) {
          const defaultReminders = transformSettingApiToComponentReminders(response.data.reminders || [], false);
          const settingsRemindersEnabled = response.data.remindersEnabled || false;
          const isPartialPaymentEnabled = response.data.partialPaymentStatus !== PartialPaymentOption.OFF_BY_DEFAULT;

          // Store default reminders and settings config in Redux
          dispatch(
            updateInvoiceDefaultReminderFromSetting({
              reminders: defaultReminders,
              enabled: settingsRemindersEnabled,
              partialPayment: response.data.partialPaymentStatus
            })
          );

          if (!hasUserAndLineItemsData) {
            const dueDateDuration = response.data.dueDate?.duration;
            const dueDateISO = getDueDate(invoiceDate, dueDateDuration)?.toISOString();
            dispatch(updateDueDate(dueDateISO));

            const updatedReminders = recalculateReminderDates(defaultReminders, dueDateISO);
            dispatch(updatePaymentReminders(updatedReminders));

            if (settingsRemindersEnabled) {
              dispatch(updatePaymentRemindersEnabled(settingsRemindersEnabled));
            }
            dispatch(updatePartialPaymentEnabled(isPartialPaymentEnabled));
          }
        }
      });
    }
  }, [currentTeam.id, dispatch, isInvoiceFromJob, isInvoiceFromEstimate]);

  useEffect(() => {
    if (merchantCustomizationsError) {
      logAnalyticEvent('webapp_payment_settings_error', {
        errorCode: merchantCustomizationsError.code,
        errorMessage: JSON.stringify(merchantCustomizationsError.message),
        errorName: merchantCustomizationsError.name
      });
      logUserNetworkData();
    }
  }, [merchantCustomizationsError, isMerchantCustomizationsError]);

  const [
    createInvoiceV2Mutation,
    {
      isSuccess: isCreateInvoiceSuccess,
      data: invoiceCreatedResponse,
      isError: isCreateInvoiceError,
      error: createInvoiceError,
      isLoading: isCreateInvoiceLoading
    }
  ] = useCreateInvoiceV2Mutation();

  const [selectedInvoiceTab, setSelectedInvoiceTab] = useState(InvoiceTabEnum.BILL_TO);

  const queryParams = new URLSearchParams(window.location.search);

  const urlQuery = queryString.parse(location.search);
  const jobId = queryParams.get('jobId');
  const estimateId = queryParams.get('estimateId');
  const [getCustomerByIdQuery, getCustomerByIdState] = useLazyGetCustomerByIdQuery();
  const { data: customerData, isSuccess: customerDataSuccess } = getCustomerByIdState;
  const [getJobByIdQuery, { isFetching: isJobByIdFetching }] = useLazyGetJobByIdQuery();

  const [getEstimateByIdQuery, { isFetching: isEstimateByIdFetching }] = useLazyGetEstimateByIdQuery();

  useEffect(() => {
    if (urlQuery.customerId) {
      const { customerId } = urlQuery;
      getCustomerByIdQuery({ teamId: currentTeam.id, customerId: customerId as string });
    }
  }, []);

  useEffect(() => {
    if (jobId && !isLoadingTaxesAndFees) {
      getJobByIdQuery({ teamId: currentTeam.id, jobId: jobId, isFromInvoice: true });
    }
  }, [jobId, currentTeam.id, isLoadingTaxesAndFees]);

  useEffect(() => {
    if (estimateId && !isLoadingTaxesAndFees) {
      getEstimateByIdQuery({ teamId: currentTeam.id, estimateId, isFromInvoice: true });
    }
  }, [estimateId, currentTeam.id, isLoadingTaxesAndFees]);

  useEffect(() => {
    if (isInvoiceFromJob && !isInitialising && !isLoading) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Customer and job details copied to invoice',
            isMessageHtml: false
          },
          showClose: true,
          type: NotifierTypes.SUCCESS
        })
      );
    }
  }, [isInitialising, isInvoiceFromJob, isLoading]);

  useEffect(() => {
    if (!isLoadingTaxesAndFees && !isInitialising && !isEstimateByIdFetching && !isJobByIdFetching) {
      setIsLoading(false);
    }
  }, [isLoadingTaxesAndFees, isInitialising, isEstimateByIdFetching, isJobByIdFetching]);

  useEffect(() => {
    if (customerDataSuccess) {
      dispatch(updateInvoiceCustomer({ ...customerData, id: null, ID: customerData?.id }));
    }
  }, [customerDataSuccess]);

  useEffect(() => {
    if (isCreateInvoiceSuccess) {
      dispatch(resetInvoiceState());
      dispatch(updateInvoiceCreatedResponse(invoiceCreatedResponse));
      if (location.state?.returnTo) {
        navigate(location.state.returnTo, {
          state: location.state.returnToState || null,
          replace: true
        });
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Invoice created successfully!',
              secondaryMessage: '',
              isMessageHtml: false
            },
            type: NotifierTypes.SUCCESS,
            showClose: true
          })
        );
      } else {
        const invoiceRouteState: InvoicesRouteState = {
          showShareModal: true,
          isEditInvoiceShare: false,
          showSidePanel: false
        };
        navigate(routesPath.INVOICES, { replace: true, state: invoiceRouteState });
      }
    }
  }, [isCreateInvoiceSuccess]);

  useEffect(() => {
    if (isCreateInvoiceError) {
      const error = createInvoiceError as ErrorResponse;
      if (error?.data?.details) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: error.data.details,
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      } else {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Something went wrong',
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      }
    }
  }, [isCreateInvoiceError]);

  const handleScrollToTab = (invoiceTab: InvoiceTabEnum) => {
    const tabElem = document.getElementById(invoiceTab);
    if (tabElem) {
      tabElem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
  };

  useEffect(() => {
    handleScrollToTab(selectedInvoiceTab);
  }, [selectedInvoiceTab]);

  const onPreviewButtonClick = () => {
    let isPageIncomplete = false;
    if (selectedInvoice.invoiceLineItems.length === 0) {
      isPageIncomplete = true;
      dispatch(addInvoiceValidationError(minimumLineItemsError));
    }
    if (selectedInvoice.invoiceCustomer === null) {
      isPageIncomplete = true;
      dispatch(enableCustomerNotSelectedError());
    }

    if (isPageIncomplete) {
      return;
    }

    onPreviewClick();
  };

  const hasDataChanges = useMemo(() => {
    return hasChangesToSync(
      selectedInvoice.invoiceCustomer,
      selectedInvoice.initialInvoiceCustomer,
      selectedInvoice.invoiceLineItems,
      selectedInvoice.initialInvoiceLineItems,
      selectedInvoice.selectedServiceCharge,
      selectedInvoice.initialServiceCharge,
      selectedInvoice.invoiceSubTotal,
      selectedInvoice.initialInvoiceSubTotal,
      selectedInvoice.isServiceFeeEnabled,
      selectedInvoice.isServiceFeeEnabledInitial,
      selectedInvoice.currentSurcharge,
      selectedInvoice.initialSurcharge
    );
  }, [
    selectedInvoice.invoiceCustomer,
    selectedInvoice.initialInvoiceCustomer,
    selectedInvoice.invoiceLineItems,
    selectedInvoice.initialInvoiceLineItems,
    selectedInvoice.selectedServiceCharge,
    selectedInvoice.initialServiceCharge,
    selectedInvoice.invoiceSubTotal,
    selectedInvoice.initialInvoiceSubTotal,
    selectedInvoice.isServiceFeeEnabled,
    selectedInvoice.isServiceFeeEnabledInitial,
    selectedInvoice.currentSurcharge,
    selectedInvoice.initialSurcharge
  ]);

  const handleCreateInvoiceV2 = useCallback(
    (entityData?: RelatedEntityData) => {
      const v2Payload = getCreateInvoiceV2Payload(
        selectedInvoice,
        currentTeam,
        taxesAndFeesData?.tnc,
        merchantCustomisations,
        entityData
      );

      createInvoiceV2Mutation({ teamId: currentTeam.id.toString(), payload: v2Payload });
    },
    [selectedInvoice, currentTeam, taxesAndFeesData?.tnc, merchantCustomisations, createInvoiceV2Mutation]
  );

  const handleCreateInvoiceFromOtherEntities = useCallback(() => {
    let entityData: RelatedEntityData | undefined;

    if (isInvoiceFromEstimate) {
      entityData = { entityId: estimateId, entityType: 'ESTIMATE', entityNumber: invoiceFromEstimateNumber };
    } else if (isInvoiceFromJob) {
      entityData = { entityId: jobId, entityType: 'JOB', entityNumber: invoiceFromJobNumber };
    }

    handleCreateInvoiceV2(entityData);
  }, [
    isInvoiceFromEstimate,
    estimateId,
    invoiceFromEstimateNumber,
    isInvoiceFromJob,
    jobId,
    invoiceFromJobNumber,
    handleCreateInvoiceV2
  ]);

  const onCreateAndShare = useCallback(() => {
    if (isActionsBlocked) {
      setShowMerchantBlockedPopup(true);
      return;
    }
    let isPageIncomplete = false;
    let errors = InvoiceErrorFieldsDefault;
    if (selectedInvoice.invoiceLineItems.length === 0) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    if (selectedInvoice.invoiceCustomer === null) {
      isPageIncomplete = true;
      errors = { ...errors, customer: true };
    }
    const invoiceMinAmount = +merchantCustomisations?.minimumAmount || invoiceMinimumAmount;
    const invoiceMaxAmount = +merchantCustomisations?.maximumAmount;
    if (selectedInvoice.finalAmount < invoiceMinAmount) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    if (selectedInvoice.finalAmount > invoiceMaxAmount && !partialPaymentEnabled) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    const isSelfCheckoutEmpty =
      selectedInvoice.invoicePaymentModes.filter(item => item.isSelected && item.teamLevelVisiblity).length === 0;

    if (isSelfCheckoutEmpty) {
      const isAnyPaymentModeEligible = checkIfAnyPaymentModeIsEligible(
        merchantCustomisations?.paymentModes,
        selectedInvoice.finalAmount,
        partialPaymentEnabled
      );
      if (!isAnyPaymentModeEligible) {
        isPageIncomplete = true;
        errors = { ...errors, paymentMethods: true };
      }
    }

    if (isPageIncomplete) {
      setShowInvoiceErrorsPopup({ open: true, errors });
      return;
    }

    if (isSelfCheckoutEmpty) {
      setShowNoSelfCheckoutPopup(true);
      return;
    }

    // Check if partial payment is enabled but invoice total exceeds invoice limit
    if (partialPaymentEnabled && invoiceTotal > 0 && invoiceTotal > +invoiceLimits.invoiceMaxLimit) {
      setShowInvoiceLimitExceededPopup(true);
      return;
    }

    if (partialPaymentEnabled && invoiceTotal > 0) {
      const exceedsLimit = checkIfInvoiceTotalExceedsPaymentModeLimit(invoiceTotal, invoicePaymentModes);
      if (exceedsLimit) {
        setShowPartialPaymentAvailablePopup(true);
        return;
      }
    }

    const showSyncConfirmation = (isInvoiceFromJob || (isInvoiceFromEstimate && isEstimatePending)) && hasDataChanges;

    if (showSyncConfirmation) {
      setShowSyncConfirmationPopup(true);
    } else if (isInvoiceFromEstimate || isInvoiceFromJob) {
      handleCreateInvoiceFromOtherEntities();
    } else {
      handleCreateInvoiceV2();
    }
  }, [
    isActionsBlocked,
    selectedInvoice.invoiceLineItems.length,
    selectedInvoice.invoiceCustomer,
    selectedInvoice.finalAmount,
    merchantCustomisations?.minimumAmount,
    merchantCustomisations?.maximumAmount,
    partialPaymentEnabled,
    selectedInvoice.invoicePaymentModes,
    merchantCustomisations?.paymentModes,
    invoiceTotal,
    invoiceLimits.invoiceMaxLimit,
    invoicePaymentModes,
    isInvoiceFromJob,
    isInvoiceFromEstimate,
    isEstimatePending,
    hasDataChanges,
    handleCreateInvoiceFromOtherEntities,
    handleCreateInvoiceV2
  ]);

  const handleOnCreateInvoiceWithoutSelfCheckout = useCallback(() => {
    const showSyncConfirmation = (isInvoiceFromJob || (isInvoiceFromEstimate && isEstimatePending)) && hasDataChanges;
    if (showSyncConfirmation) {
      setShowSyncConfirmationPopup(true);
    } else if (isInvoiceFromEstimate || isInvoiceFromJob) {
      handleCreateInvoiceFromOtherEntities();
    } else {
      handleCreateInvoiceV2();
    }
  }, [
    isInvoiceFromJob,
    isInvoiceFromEstimate,
    isEstimatePending,
    hasDataChanges,
    handleCreateInvoiceFromOtherEntities,
    handleCreateInvoiceV2
  ]);

  const handleOnEditPaymentMethods = useCallback(() => {
    handleCloseNoSelfCheckoutPopup();
    handleScrollToTab(InvoiceTabEnum.PAYMENT_OPTIONS);
  }, []);

  const handleCloseNoSelfCheckoutPopup = useCallback(() => {
    setShowNoSelfCheckoutPopup(false);
  }, []);

  const handleCloseInvoiceErrorsPopup = useCallback(() => {
    setShowInvoiceErrorsPopup({ open: false, errors: InvoiceErrorFieldsDefault });
  }, []);

  const handleClosePartialPaymentAvailablePopup = useCallback(() => {
    setShowPartialPaymentAvailablePopup(false);
  }, []);

  const handleCreateAnyway = useCallback(() => {
    handleClosePartialPaymentAvailablePopup();
    const showSyncConfirmation = (isInvoiceFromJob || (isInvoiceFromEstimate && isEstimatePending)) && hasDataChanges;
    if (showSyncConfirmation) {
      setShowSyncConfirmationPopup(true);
    } else if (isInvoiceFromEstimate || isInvoiceFromJob) {
      handleCreateInvoiceFromOtherEntities();
    } else {
      handleCreateInvoiceV2();
    }
  }, [
    isInvoiceFromJob,
    isInvoiceFromEstimate,
    isEstimatePending,
    hasDataChanges,
    handleCreateInvoiceFromOtherEntities,
    handleCreateInvoiceV2
  ]);

  const handleEditInvoiceFromPartialPaymentPopup = useCallback(() => {
    handleClosePartialPaymentAvailablePopup();
    handleScrollToTab(InvoiceTabEnum.PAYMENT_OPTIONS);
  }, []);

  const handleCloseInvoiceLimitExceededPopup = useCallback(() => {
    setShowInvoiceLimitExceededPopup(false);
  }, []);

  const handleEditInvoiceFromLimitExceededPopup = useCallback(() => {
    handleCloseInvoiceLimitExceededPopup();
  }, []);

  const createAndShareHandler = useCallback(
    throttle(() => {
      onCreateAndShare();
    }, 800),
    [onCreateAndShare]
  );

  const onBackIconClick = () => {
    navigate(-1);
    dispatch(resetInvoiceState());
  };

  const handleSignatureStateChange = (value: boolean) => {
    dispatch(updateIsSignatureEnabled(value));
  };

  const handleInvoiceReferenceNumberChange = (value: string) => {
    dispatch(updateInvoiceReferenceNumber(value));
  };

  const checkIsSignatureDisabled = useMemo(() => {
    if (merchantCustomisationsSuccess) {
      return merchantCustomisations.customerSignatureConfig === FeatureToggleState.ALWAYS_ON;
    }
    return false;
  }, [merchantCustomisationsSuccess, merchantCustomisations?.customerSignatureConfig]);

  const syncPopupTitle = useMemo(() => {
    return `Save and sync changes to the related ${isInvoiceFromJob || hasEstimateRelatedJob ? 'job' : ''} ${
      (hasEstimateRelatedJob || isInvoiceFromJob) && (isInvoiceFromEstimate || isEstimatePending) ? 'and' : ''
    } ${isInvoiceFromEstimate || isEstimatePending ? 'estimate' : ''}?`;
  }, [isInvoiceFromJob, hasEstimateRelatedJob, isInvoiceFromEstimate, isEstimatePending]);

  const syncPopupSubtitle = useMemo(() => {
    return `Changes in your invoices will be applied to the associated ${
      isInvoiceFromJob || hasEstimateRelatedJob ? `job ${invoiceFromJobNumber}` : ''
    } ${(hasEstimateRelatedJob || isInvoiceFromJob) && (isInvoiceFromEstimate || isEstimatePending) ? 'and' : ''} ${
      isInvoiceFromEstimate || isEstimatePending ? `estimate ${invoiceFromEstimateNumber}` : ''
    } as well.`;
  }, [
    isInvoiceFromJob,
    hasEstimateRelatedJob,
    invoiceFromJobNumber,
    isInvoiceFromEstimate,
    isEstimatePending,
    invoiceFromEstimateNumber
  ]);

  return (
    <CreateInvoiceContext.Provider value={{ selectedInvoiceTab, setSelectedInvoiceTab }}>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="flex h-full w-full flex-col border">
          <PreviewBar
            className="border border-borderGray"
            title="New Invoice"
            onPrimaryClick={createAndShareHandler}
            onReturn={onBackIconClick}
            primaryBtnTitle="Create & Share"
            disablePrimaryBtn={false}
            secondaryBtnTitle="Preview"
            onSecondaryClick={onPreviewButtonClick}
            primaryBtnId="create-and-share-invoice-button"
            secondaryBtnId="create-invoice-preview-button"
          />
          <div className="flex h-full w-full flex-row gap-x-[120px] overflow-hidden bg-primary">
            <div
              className="hidden w-1/3 max-w-[31.25rem] flex-col items-center p-11 pr-0
          lg:flex lg:w-80">
              <InvoicesTabSideNav context={CreateInvoiceContext} />
            </div>

            <div
              id="scrollable div"
              className="customNormalScroll flex grow flex-col space-y-8 overflow-y-auto p-12 pl-0">
              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.BILL_TO}>
                <CreateInvoiceCustomer context={CreateInvoiceContext} />
              </div>

              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.INVOICE_OPTIONS}>
                <InvoiceOptionsSection context={CreateInvoiceContext} />
              </div>

              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.LINE_ITEMS}>
                <LineItemsSection isLoadingTaxesAndFees={isLoadingTaxesAndFees} context={CreateInvoiceContext} />
              </div>

              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.NOTES_ATTACHMENTS}>
                <NotesAttachmentsSection
                  context={CreateInvoiceContext}
                  isLoadingTaxesAndFees={isLoadingTaxesAndFees}
                  taxesAndFees={taxesAndFeesData}
                />
              </div>

              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.PAYMENT_OPTIONS}>
                <PaymentOptionsSection taxesAndFees={taxesAndFeesData} context={CreateInvoiceContext} />
              </div>
              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.PAYMENT_REMINDERS}>
                <InvoiceFormRemindersSection context={CreateInvoiceContext} />
              </div>
              <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.MORE_OPTIONS}>
                <MoreOptionsSection
                  updateSignatureState={handleSignatureStateChange}
                  signatureState={selectedIsSignatureEnabled}
                  context={CreateInvoiceContext}
                  isDisabled={checkIsSignatureDisabled}
                  id="create-invoice-allow-sign-toggle-switch"
                  invoiceReferenceNumber={invoiceReferenceNumber}
                  updateInvoiceReferenceNumber={handleInvoiceReferenceNumberChange}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {showSyncConfirmationPopup && (
        <GenericModal
          showModal={true}
          title={syncPopupTitle}
          titleStyle="text-center"
          subTitleStyle="text-center text-primaryText text-sbase font-normal"
          subTitle={syncPopupSubtitle}
          primaryBtnLabel="Yes, continue"
          primaryBtnId="sync-job-invoice"
          primaryBtnStyle="mt-2"
          onPrimaryBtnClick={handleCreateInvoiceFromOtherEntities}
          secondaryBtnId="cancel-sync-job-invoice"
          secondaryBtnStyle="border-none shadow-none hover:bg-transparent"
          secondaryBtnLabel="Cancel"
          onSecondaryBtnClick={() => setShowSyncConfirmationPopup(false)}
          closeIconStyle="top-3 right-3"
          CloseIcon={() => (
            <CrossIconvector
              height={18}
              width={18}
              title="sync-popup-close"
              onClick={() => setShowSyncConfirmationPopup(false)}
              className="cursor-pointer"
            />
          )}
          isActionInProgress={isCreateInvoiceLoading}
          height="min-h-[262px]"
          className="!px-5 !py-6"
        />
      )}
      {showNoSelfCheckoutPopup && (
        <NoSelfCheckoutPopup
          isEdit={false}
          onContinue={handleOnCreateInvoiceWithoutSelfCheckout}
          onEditPaymentMethods={handleOnEditPaymentMethods}
          onClose={handleCloseNoSelfCheckoutPopup}
          isActionLoading={isCreateInvoiceLoading}
        />
      )}
      {showInvoiceErrorsPopup.open && (
        <InvoiceErrorsPopup
          onClose={handleCloseInvoiceErrorsPopup}
          errorFields={showInvoiceErrorsPopup.errors}
          maximumAmount={
            partialPaymentEnabled ? +invoiceLimits.invoiceMaxLimit : +merchantCustomisations?.maximumAmount || 0
          }
          minimumAmount={
            partialPaymentEnabled ? +invoiceLimits.invoiceMinLimit : +merchantCustomisations?.minimumAmount || 0
          }
          onContinue={handleCloseInvoiceErrorsPopup}
        />
      )}
      {showMerchantBlockedPopup && (
        <BlockedInfoPopup isOpen={showMerchantBlockedPopup} onClose={() => setShowMerchantBlockedPopup(false)} />
      )}
      {showPartialPaymentAvailablePopup && (
        <PartialPaymentAvailablePopup
          onClose={handleClosePartialPaymentAvailablePopup}
          paymentModes={invoicePaymentModes}
          onCreateAnyway={handleCreateAnyway}
          onEditInvoice={handleEditInvoiceFromPartialPaymentPopup}
        />
      )}
      {showInvoiceLimitExceededPopup && (
        <InvoiceLimitExceededPopup
          onClose={handleCloseInvoiceLimitExceededPopup}
          invoiceLimits={invoiceLimits}
          onEditInvoice={handleEditInvoiceFromLimitExceededPopup}
        />
      )}
    </CreateInvoiceContext.Provider>
  );
};

export default CreateInvoice;
